import { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import SectionHeading from '../components/SectionHeading';
import Reveal from '../components/Reveal';
import Button from '../components/Button';

const videos = [
  { src: '/assets/video/einsatz-01.mp4', poster: '/assets/img/video-posters/einsatz-01.webp', title: 'Betonpumpe auf engem Raum' },
  { src: '/assets/video/einsatz-02.mp4', poster: '/assets/img/video-posters/einsatz-02.webp', title: 'Maschine und Fahrer im Einsatz' },
  { src: '/assets/video/einsatz-03.mp4', poster: '/assets/img/video-posters/einsatz-03.webp', title: 'Baustellenablauf in Bewegung' },
];

function LazyVideo({ src, poster, title }) {
  const videoRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.poster = poster;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !loaded) {
            const source = video.querySelector('source');
            if (source && source.dataset.src) {
              source.src = source.dataset.src;
              source.removeAttribute('data-src');
              video.load();
              setLoaded(true);
            }
            observer.unobserve(video);
          }
        });
      },
      { rootMargin: '480px 0px', threshold: 0.01 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [loaded, poster]);

  return (
    <article className="min-w-0 p-2 rounded-card border border-ink/10 bg-white/90 shadow-[0_16px_40px_rgba(13,37,46,0.08)] transition-all hover:-translate-y-1 hover:border-signal/40 hover:shadow-[0_18px_42px_rgba(13,37,46,0.12)]">
      <video
        ref={videoRef}
        controls
        preload="none"
        aria-label={title}
        className="w-full aspect-[9/16] max-h-[min(520px,72svh)] md:max-h-[620px] object-cover rounded-md bg-[#0b1115]"
      >
        <source data-src={src} type="video/mp4" />
      </video>
      <h3 className="text-sm sm:text-base font-extrabold mt-3 mb-1 px-1">{title}</h3>
    </article>
  );
}

function VideoSection() {
  return (
    <section id="videos" className="py-12 sm:py-16 md:py-28 border-t border-ink/10">
      <div className="container-site mx-auto">
        <Reveal>
          <SectionHeading
            eyebrow="Videos"
            title="MH-BETON im laufenden Einsatz."
          >
            <div>
              <p className="mb-4 text-sm sm:text-base">
                Kurze Einblicke in echte Einsätze: Betonpumpe, Maschine und Baustellenablauf direkt aus der Praxis.
              </p>
              <Button href="https://www.tiktok.com/@mhbeton" target="_blank" rel="noopener" variant="quiet">
                <ExternalLink className="w-4 h-4" />
                TikTok öffnen
              </Button>
            </div>
          </SectionHeading>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {videos.map((video, index) => (
            <Reveal key={video.src} delay={index * 100}>
              <LazyVideo {...video} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default VideoSection;
